/**
 * Pure component for confirming student details as part of the new user flow.
 */
import React, { FunctionComponent, useEffect, useState } from 'react';
import classNames from 'classnames';
import NewUserButton from './common/new-user-button';
import NewUserPasswordInput from './common/new-user-password-input';
import NewUserInput from './common/new-user-input';
import { VelocityTransitionGroup } from 'velocity-react';

interface IConfirmDetailsViewProps {
    userName: string; // Username provided to the user
    password: string; // Password the user chose/was given
    typedUsername: string;
    typedPassword: string;
    onSubmit: () => void; // Handle form submit
    onBack: () => void; // Handle back button press
    usingChoosePassword: boolean; // If using V2 display some additional text on the page
    updateViewState: (username: string, password: string) => void;
    disableAnimations: boolean; // Disable the animations for testing
}

const validationMessage = (message: string): JSX.Element => (
    <div className="new-user-view-validation-message new-user-confirmation-validation-message">
        <span className="new-user-view-validation-title">
            <i className="fas fa-exclamation-triangle" />
        </span>{' '}
        {message}
    </div>
);

const ConfirmDetailsView: FunctionComponent<IConfirmDetailsViewProps> = ({
    updateViewState,
    userName,
    password,
    onSubmit,
    onBack,
    typedPassword,
    typedUsername,
    disableAnimations,
}: IConfirmDetailsViewProps) => {
    const [confirmedPassword, setConfirmedPassword] = useState<string>('');
    const [confirmedUsername, setConfirmedUsername] = useState<string>('');

    const [passwordMismatchError, setPasswordMismatchError] = useState<boolean>(false);
    const [usernameMismatchError, setUsernameMismatchError] = useState<boolean>(false);

    useEffect(
        () => {
            setConfirmedPassword(typedPassword);
            setConfirmedUsername(typedUsername);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // Check the username matches the username given to them. Case insensitive check to
    // make it less annoying for the user
    const userNamesMatch = (): boolean => {
        if (confirmedUsername.trim() === '') {
            return false;
        }

        return userName.toLowerCase() === confirmedUsername.toLowerCase();
    };

    // Shows up under inputs when incorrect details provided
    const validationFailed = (showError: boolean, message: string): JSX.Element => {
        if (!disableAnimations) {
            return (
                <VelocityTransitionGroup
                    enter={{ animation: 'slideDown' }}
                    leave={{ animation: 'slideUp' }}
                >
                    {showError && validationMessage(message)}
                </VelocityTransitionGroup>
            );
        } else {
            return <span>{showError && validationMessage(message)}</span>;
        }
    };

    const usernameField: JSX.Element = (
        <NewUserInput
            name="username"
            label="Enter your username:"
            classes={classNames('new-user-view-form-group-confirm-username', {
                'no-error': !usernameMismatchError,
            })}
            value={confirmedUsername}
            validationError={usernameMismatchError}
            handleChange={value => {
                setConfirmedUsername(value);

                if (usernameMismatchError && userName.toLowerCase() === value.toLowerCase()) {
                    setUsernameMismatchError(false);
                }
            }}
        >
            {validationFailed(
                usernameMismatchError,
                'The username you entered isn\u2019t right, try again or go back to see what it is',
            )}
        </NewUserInput>
    );

    const passwordField: JSX.Element = (
        <NewUserPasswordInput
            classes={classNames('new-user-view-form-group-confirm-password', {
                'no-error': !passwordMismatchError,
            })}
            label="Enter your password:"
            password={confirmedPassword}
            handleChange={value => {
                setConfirmedPassword(value);

                if (passwordMismatchError && value === password) {
                    setPasswordMismatchError(false);
                }
            }}
            validationError={passwordMismatchError}
        >
            {validationFailed(
                passwordMismatchError,
                'That password doesn\u2019t match the one you entered, try again or go back to set a different password',
            )}
        </NewUserPasswordInput>
    );

    // Check that the user details provided match the ones that are provided as props.
    // If not, set booleans in the state to show validation errors
    const validateDetails = (): boolean => {
        setUsernameMismatchError(!userNamesMatch());
        setPasswordMismatchError(password !== confirmedPassword);

        return userNamesMatch() && password === confirmedPassword;
    };

    return (
        <div className="new-user-confirm-login-details-page">
            <div className="new-user-view-content">
                <div className="new-user-view-box username-reveal">
                    <p className="new-user-username-reveal-header">
                        Let&apos;s check you have remembered your log in details
                    </p>

                    <form
                        onSubmit={e => {
                            e.preventDefault();

                            if (validateDetails()) {
                                onSubmit();
                            }
                        }}
                    >
                        {usernameField}
                        {passwordField}
                        <div className="new-user-view-form-button-group new-user-choose-password-btn-group new-user-confirm-details-button-group">
                            <NewUserButton
                                type="button"
                                value="Back"
                                handleClick={() => {
                                    updateViewState(confirmedUsername, confirmedPassword);

                                    onBack();
                                }}
                            />

                            <NewUserButton
                                type="submit"
                                value="Check your details"
                                handleClick={() => validateDetails() && onSubmit()}
                                showIcon={true}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDetailsView;
